<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

import JobsearchResultModeAnalysis from './JobsearchResultModeAnalysis.vue';
import JobsearchResultModeList from './JobsearchResultModeList.vue';
import JobsearchResultModeMap from './JobsearchResultModeMap.vue';
import JobsearchResultModeUnsolicited from './JobsearchResultModeUnsolicited.vue';
import JobsearchResultTopBar from './JobsearchResultTopBar.vue';
import JobsearchResultBottomBar from './JobsearchResultBottomBar.vue';

import { setDocumentTitle, setCanonicalUrl, setRssFeedUrl, setMetaDescription } from 'JIX/utils.js';
import { useNavigationHistory } from 'JIX/useNavigationHistory.js';

import { __ } from 'JIX/gettext.js';

const store = useStore();

const site = ref(window.Stash.common.site);
// Setup Piwik datalayer
window._paq = window._paq || [];

watch(() => store.getters.workingQuery, () => {
    store.dispatch('fetchHitcount');
}, { deep: 2 });

function sendAnalyticsEvent() {
    // The join-logic here should match the logic found related to jix_analytics_jobcategories in tmpl/mojo/page/head/google_analytics.html.ep
    const mostFrequentSubjobcategoryids = ',' + (store.state.searchResponse.most_frequent_subjobcategoryids ?? []).join(',') + ',';
    window._paq.push(['setCustomDimensionValue', 9, mostFrequentSubjobcategoryids]);
    if (window.jixAnalytics) {
        window.jixAnalytics('pageview', { path: location.pathname + location.search, title: store.state.searchResponse.title });
    }
    if (window.pp_gemius_identifier2 && window.pp_gemius_hit) {
        window.pp_gemius_hit(window.pp_gemius_identifier2);
    }
}

const url = computed(() => store.state.searchResponse.link_canonical);

useNavigationHistory({
    url,
    state: computed(() => { return { searchQuery: store.state.searchQuery, viewMode: store.state.viewMode }; }),
    onPop({ state }) {
        store.commit('setSearchQuery', state.searchQuery);
        store.dispatch('performSearch', { newViewMode: state.viewMode });
    },
});

onMounted(() => {
    watch(
        url,
        (newUrl) => {
            setCanonicalUrl(newUrl);
            sendAnalyticsEvent();
        },
    );

    watch(
        () => store.state.searchResponse.meta_description,
        (value) => setMetaDescription(value),
    );

    watch(
        () => store.state.searchResponse.link_rss,
        (url) => {
            setRssFeedUrl(url);
        },
        { immediate: true },
    );

    watch(
        () => store.state.searchResponse.title,
        (title) => setDocumentTitle(title),
    );
});

const viewModeComponent = computed(() => {
    switch (store.state.viewMode) {
        case "analysis":
            return JobsearchResultModeAnalysis;
        case "list":
            return JobsearchResultModeList;
        case "map":
            return JobsearchResultModeMap;
        case "unsolicited":
            return JobsearchResultModeUnsolicited;
        default:
            throw new Error("Unknown view mode");
    }
});
</script>
<template>
    <JobsearchResultTopBar />
    <div
        class="position-relative jobsearch-results"
        :class="store.getters.requestInProgress ? 'jobsearch-results--loading' : ''"
    >
        <Component :is="viewModeComponent" />
    </div>
    <JobsearchResultBottomBar v-if="site === 'jobdk'" />
</template>
